import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineLockClosed, HiOutlineUserGroup } from 'react-icons/hi'
import BaseModal from '../../modals/BaseModal/BaseModal'
import Loading from '../../Loading'
import { VISIBILITY_PRIVATE, VISIBILITY_SCHOOL } from './templateUtils'

const MODAL_CONTENT_STYLE = { width: 'min(100%, 540px)' }

const OPTIONS = [
    { value: VISIBILITY_PRIVATE, Icon: HiOutlineLockClosed, title: 'templateVisibilityPrivate', body: 'templateVisibilityPrivateBody' },
    { value: VISIBILITY_SCHOOL, Icon: HiOutlineUserGroup, title: 'templateVisibilitySchool', body: 'templateVisibilitySchoolBody' },
]

/**
 * The one extra step of "Save as template": who can find and use it.
 *
 * `onConfirm(visibility)` runs the save and resolves to true on success; the
 * modal keeps itself open (and the choice intact) when it resolves false so the
 * user can retry. "Only me" is the default for new templates; an existing
 * template opens on its current visibility, which also makes editing a way to
 * share or stop sharing it.
 */
function SaveTemplateModal({ isOpen, onClose, onConfirm, mode = 'create', initialVisibility = VISIBILITY_PRIVATE, editingAuthorName = '' }) {
    const { t } = useTranslation()
    const [visibility, setVisibility] = useState(initialVisibility)
    const [saving, setSaving] = useState(false)
    const optionRefs = useRef({})
    const mountedRef = useRef(true)

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    // Every opening starts from the template's own state, not the last choice.
    useEffect(() => {
        if (isOpen) {
            setVisibility(initialVisibility)
            setSaving(false)
        }
    }, [isOpen, initialVisibility])

    const close = () => {
        if (!saving) onClose()
    }

    const confirm = async () => {
        if (saving) return
        setSaving(true)
        try {
            await onConfirm(visibility)
        } finally {
            if (mountedRef.current) setSaving(false)
        }
    }

    // Native radio-group keyboard model: arrows move and select, Tab leaves the group.
    const onKeyDown = (event) => {
        const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key]
        if (!step) return
        event.preventDefault()
        const index = OPTIONS.findIndex((option) => option.value === visibility)
        const next = OPTIONS[(index + step + OPTIONS.length) % OPTIONS.length].value
        setVisibility(next)
        optionRefs.current[next]?.focus()
    }

    const isUpdate = mode === 'update'

    return (
        <BaseModal isOpen={isOpen} onClose={close} contentStyle={MODAL_CONTENT_STYLE} maxWidth={540} ariaLabel={t(isUpdate ? 'updateTemplate' : 'saveAsTemplate')} zIndex={101}>
            <div className="coc-save-modal px-8 pt-10 pb-8 text-left">
                <h2 className="text-deepPurple font-bold text-2xl">{t(isUpdate ? 'updateTemplate' : 'saveAsTemplate')}</h2>
                <p className="text-mudPurple text-base mt-1" id="template-visibility-label">
                    {t('templateVisibilityQuestion')}
                </p>

                <div className="flex flex-col gap-3 mt-6" role="radiogroup" aria-labelledby="template-visibility-label" onKeyDown={onKeyDown}>
                    {OPTIONS.map(({ value, Icon, title, body }) => {
                        const selected = value === visibility
                        return (
                            <button
                                key={value}
                                ref={(node) => (optionRefs.current[value] = node)}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                tabIndex={selected ? 0 : -1}
                                id={`template-visibility-${value}`}
                                className="coc-choice w-full flex items-center gap-4 rounded-xl p-4 text-left"
                                onClick={() => setVisibility(value)}
                            >
                                <span className="coc-choice-icon flex items-center justify-center rounded-full flex-shrink-0" aria-hidden="true">
                                    <Icon size={22} />
                                </span>
                                <span className="flex-1 min-w-0">
                                    <span className="block text-deepPurple font-bold text-lg leading-6">{t(title)}</span>
                                    <span className="block text-mudPurple text-sm leading-5 mt-0.5">{t(body)}</span>
                                </span>
                                <span className="coc-choice-radio flex-shrink-0" aria-hidden="true" />
                            </button>
                        )
                    })}
                </div>

                {!!editingAuthorName && (
                    <p className="coc-note text-sm text-darkenPurple rounded-xl px-4 py-3 mt-4">{t('templateEditingColleagueNote', { name: editingAuthorName })}</p>
                )}

                <div className="coc-save-modal-actions flex gap-3 mt-8">
                    <button
                        type="button"
                        id="save-template-cancel-button"
                        className="coc-button coc-button-outline !border !border-solid rounded-full py-3 flex-1 font-semibold text-lg"
                        onClick={close}
                        disabled={saving}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        id="save-template-confirm-button"
                        className="coc-button coc-button-primary rounded-full py-3 flex-1 flex items-center justify-center font-semibold text-lg"
                        onClick={confirm}
                        disabled={saving}
                        aria-busy={saving}
                    >
                        {saving ? <Loading /> : t(isUpdate ? 'updateTemplate' : 'saveTemplateConfirm')}
                    </button>
                </div>
            </div>
        </BaseModal>
    )
}

export default SaveTemplateModal
